import {useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import {object} from 'yup';

import {Button, Form} from '#component/base';
import {PasswordInputField} from '#component/partial';
import {sprinkles} from '#style/sprinkles';
import {passwordValidator, stringValidator} from '#util/validator';

import {ChangePasswordFormProps, FieldValues, SubmitHandler} from './ChangePasswordForm.types';

const ChangePasswordFormConfig = {
  mode: 'onBlur',
  resolver: yupResolver(
    object({
      currentPassword: stringValidator.required('Please enter your current password'),
      newPassword: passwordValidator,
    }),
  ),
} as const;

export const ChangePasswordForm = ({className, onError, onSubmit}: ChangePasswordFormProps) => {
  const {
    reset,
    clearErrors,
    formState: {errors, isSubmitting},
    handleSubmit,
    register,
    setError,
  } = useForm<FieldValues>(ChangePasswordFormConfig);

  const handleChangePassword: SubmitHandler = async ({currentPassword, newPassword}) => {
    try {
      await onSubmit({currentPassword, newPassword});
      reset();
    } catch (error) {
      const isInvalidPassword = (error as Error)?.message === 'Unauthorized';

      if (isInvalidPassword) {
        setError('currentPassword', {message: 'Current password is incorrect'});
      } else {
        setError('form', {
          message: 'There was an issue editing your password. Please contact customer support.',
        });
      }
      onError?.(error as any as Error);
    }
  };

  return (
    <Form
      className={className}
      clearErrors={clearErrors}
      errors={errors}
      onSubmit={handleSubmit(handleChangePassword)}>
      <div className={sprinkles({flex: 'flex', direction: 'col', gap: 32})}>
        <PasswordInputField
          errors={errors}
          label="Current password"
          {...register('currentPassword')}
        />

        <PasswordInputField
          errors={errors}
          label="New password"
          labelRightContent="8 characters or longer"
          {...register('newPassword')}
        />
      </div>

      <Button disabled={isSubmitting} type="submit" icon="Check">
        Confirm
      </Button>
    </Form>
  );
};
